import { toMatrix } from "@/dialogs/share-dialog/components/versions/create-or-edit-version/variables/data-grid";
import { getVariableValue } from "@/shared/utils/resolve-tokens";
import {
  type ITableSettings,
  parseTableDataDynamicVar,
} from "@library-elements/table";

/**
 * A whole-table variable binding seen from version mode: a `data` variable's
 * matrix (owns cells, rows and columns) or a legacy list variable with rows.
 */
export type TableVersionBinding =
  | { kind: "data"; name: string; matrix: string[][] }
  | { kind: "list"; name: string };

type BindingSettings = {
  dataDynamicVariable?: string;
  listDynamicVariable?: string;
  versionStatic?: boolean;
};

/** `{{deal_rows}}` → `deal_rows` (the binding keeps the token form so the deck
 * scan still collects the variable). */
export const bindingVariableName = (binding: string): string =>
  binding
    .trim()
    .replace(/^\{\{\{?\s*/, "")
    .replace(/\s*\}?\}\}$/, "");

/**
 * The binding that owns this table's data in version mode, or null when the
 * cells are the author's own (template mode, or no whole-table binding). A
 * binding locks the table even before the sender has filled the variable: the
 * runtime replaces the cells (data) or clones the template row per record
 * (list) from the variable's value, so any edit made in the table itself would
 * never reach the live link.
 */
export const getTableVersionBinding = (
  settings: BindingSettings | undefined,
  isVersion: boolean,
  versionData: Record<string, unknown> | undefined,
): TableVersionBinding | null => {
  if (!isVersion || !settings) return null;
  // A table converted to static owns its cells again even if a binding is
  // still stored (an older content copy, an API write).
  if (settings.versionStatic) return null;
  const dataBinding = settings.dataDynamicVariable;
  if (typeof dataBinding === "string" && dataBinding.trim()) {
    const name = bindingVariableName(dataBinding);
    return {
      kind: "data",
      name,
      matrix: toMatrix(getVariableValue(versionData, name)),
    };
  }
  const listVar = settings.listDynamicVariable;
  if (typeof listVar === "string" && listVar.trim())
    return { kind: "list", name: bindingVariableName(listVar) };
  return null;
};

/**
 * What the version-mode canvas should show for a bound table: the variable's
 * matrix laid over the author's styling (same parser the runtime uses), or the
 * template cells while the variable is still empty.
 */
export const resolveVersionTableSettings = (
  settings: ITableSettings,
  binding: TableVersionBinding | null,
): ITableSettings => {
  if (!binding || binding.kind !== "data" || binding.matrix.length === 0)
    return settings;
  return parseTableDataDynamicVar({ settings, matrix: binding.matrix });
};
